import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Check, Clock, Loader2, ShieldCheck } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { getDocumentService } from "@/lib/document-services";
import { createDocumentServiceOrder } from "@/lib/api/document-services.functions";
import { PayFastCheckoutButton } from "@/components/site/PayFastCheckoutButton";
import { Disclaimer } from "@/components/site/Disclaimer";

export const Route = createFileRoute("/document-services/$serviceId")({
  head: ({ params }) => {
    const s = getDocumentService(params.serviceId);
    const title = s
      ? `${s.name} — R${s.priceZar} | Nkadimeng Danny Legal`
      : "Document service | Nkadimeng Danny Legal";
    const description = s
      ? `${s.tagline} Fixed price R${s.priceZar}, delivered in ${s.turnaround}. Order online and pay securely with PayFast.`
      : "Order a fixed-price legal document online and pay securely with PayFast.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  loader: ({ params }) => {
    if (!getDocumentService(params.serviceId)) throw notFound();
    return null;
  },
  component: DocumentServiceOrderPage,
});

function DocumentServiceOrderPage() {
  const { serviceId } = Route.useParams();
  const service = getDocumentService(serviceId)!;
  const createOrder = useServerFn(createDocumentServiceOrder);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [clientName, setName] = useState("");
  const [clientEmail, setEmail] = useState("");
  const [clientPhone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState<string | null>(null);

  function set(name: string, value: string) {
    setAnswers((a) => ({ ...a, [name]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = (await createOrder({
        data: { serviceId, clientName, clientEmail, clientPhone, answers },
      })) as { reference: string };
      setReference(res.reference);
      toast.success("Order created — continue to PayFast to pay.");
    } catch (err: any) {
      toast.error("Could not create your order", {
        description: err?.message ?? String(err),
      });
    } finally {
      setSubmitting(false);
    }
  }

  const inputCls =
    "mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm";

  return (
    <section className="container-narrow py-12">
      <Link
        to="/document-services"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" /> All document services
      </Link>

      <div className="mt-6 grid gap-8 lg:grid-cols-[1.2fr_1fr]">
        <div>
          <h1 className="font-display text-3xl text-navy">{service.name}</h1>
          <p className="mt-2 text-muted-foreground">{service.tagline}</p>
          <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" /> Delivered in {service.turnaround}
          </p>

          <h2 className="mt-8 font-display text-xl text-navy">What you receive</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {service.includes.map((inc) => (
              <li key={inc} className="flex gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                <span>{inc}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8">
            <Disclaimer />
          </div>
        </div>

        <aside className="h-fit rounded-2xl border border-border bg-card p-6 shadow-soft">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-muted-foreground">Fixed price</span>
            <span className="font-display text-3xl text-navy">R{service.priceZar}</span>
          </div>

          {reference ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-md bg-muted p-3 text-sm">
                <p className="text-muted-foreground">Your reference</p>
                <p className="font-mono font-semibold text-navy">{reference}</p>
              </div>
              <PayFastCheckoutButton
                reference={reference}
                label={`Pay R${service.priceZar} with PayFast`}
              />
              <p className="text-xs text-muted-foreground">
                Work starts as soon as PayFast confirms your payment. You can track
                the order at{" "}
                <Link
                  to="/order-success/$ref"
                  params={{ ref: reference }}
                  className="underline"
                >
                  your order page
                </Link>
                .
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              {service.fields.map((f) => (
                <div key={f.name}>
                  <label className="text-sm font-medium text-navy">
                    {f.label}
                    {f.required ? " *" : ""}
                  </label>
                  {f.type === "textarea" ? (
                    <textarea
                      className={inputCls}
                      rows={4}
                      required={f.required}
                      placeholder={f.placeholder}
                      value={answers[f.name] ?? ""}
                      onChange={(e) => set(f.name, e.target.value)}
                    />
                  ) : f.type === "select" ? (
                    <select
                      className={inputCls}
                      required={f.required}
                      value={answers[f.name] ?? ""}
                      onChange={(e) => set(f.name, e.target.value)}
                    >
                      <option value="">Select…</option>
                      {(f.options ?? []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className={inputCls}
                      required={f.required}
                      placeholder={f.placeholder}
                      value={answers[f.name] ?? ""}
                      onChange={(e) => set(f.name, e.target.value)}
                    />
                  )}
                  {f.help && (
                    <p className="mt-1 text-xs text-muted-foreground">{f.help}</p>
                  )}
                </div>
              ))}

              <hr className="border-border" />

              <div>
                <label className="text-sm font-medium text-navy">Your name *</label>
                <input
                  className={inputCls}
                  required
                  value={clientName}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-navy">Email *</label>
                <input
                  type="email"
                  className={inputCls}
                  required
                  value={clientEmail}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-navy">Phone</label>
                <input
                  className={inputCls}
                  value={clientPhone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-navy px-4 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-70"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Continue to PayFast
              </button>
              <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" /> Secure PayFast checkout — we
                never see your card details.
              </p>
            </form>
          )}
        </aside>
      </div>
    </section>
  );
}
